import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Loading,
} from '@startupquickstart/frontend';
import { Search, Tag } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { PromotionalOfferStatusBadge } from '@/components/promotional-offers/PromotionalOfferStatusBadge';
import type { PromotionalOffer } from '@/config/objects/promotional-offers';

interface OfferMultiSelectDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Candidate offers to choose from. Already filtered by caller. */
  offers: PromotionalOffer[];
  isLoading?: boolean;
  /**
   * Offer ids to hide from the candidate list (already-linked, etc.). Hidden —
   * not just disabled — so the list stays focused on what the user can act on.
   */
  excludeIds?: ReadonlySet<string>;
  /** Final submission. Resolves when all selected offers have been added. */
  onSubmit: (offers: PromotionalOffer[]) => Promise<void> | void;
  submitLabel?: (count: number) => string;
  emptyMessage?: string;
}

/** Human-facing name for an offer, falling back through name → title. */
function offerLabel(offer: PromotionalOffer): string {
  return offer.name || offer.title || 'Unnamed Offer';
}

/**
 * Multi-select picker for promotional offers with checkbox rows, thumbnails and
 * client-side search. Mirrors {@link import('@/components/tours/TourMultiSelectDialog').TourMultiSelectDialog}
 * so the hotel Offers tab "Add existing" flow matches the Tours tab.
 */
export function OfferMultiSelectDialog({
  open,
  onClose,
  title,
  description,
  offers,
  isLoading = false,
  excludeIds,
  onSubmit,
  submitLabel,
  emptyMessage,
}: OfferMultiSelectDialogProps) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the modal is opened so prior selections never leak.
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedIds(new Set());
      setSubmitting(false);
    }
  }, [open]);

  const candidates = useMemo(() => {
    const filteredByExclude = excludeIds
      ? offers.filter((offer) => !excludeIds.has(offer.id))
      : offers;
    const term = search.trim().toLowerCase();
    if (!term) return filteredByExclude;
    return filteredByExclude.filter((offer) => {
      const name = offer.name?.toLowerCase() ?? '';
      const offerTitle = offer.title?.toLowerCase() ?? '';
      return name.includes(term) || offerTitle.includes(term);
    });
  }, [offers, excludeIds, search]);

  const selectedCount = selectedIds.size;

  // "Select all" tri-state is computed against the *visible* candidates only,
  // so a search filter narrows what the bulk toggle applies to.
  const visibleSelectedCount = candidates.reduce(
    (count, offer) => (selectedIds.has(offer.id) ? count + 1 : count),
    0,
  );
  const allVisibleSelected =
    candidates.length > 0 && visibleSelectedCount === candidates.length;
  const someVisibleSelected =
    visibleSelectedCount > 0 && visibleSelectedCount < candidates.length;
  const selectAllChecked: boolean | 'indeterminate' = allVisibleSelected
    ? true
    : someVisibleSelected
      ? 'indeterminate'
      : false;

  const toggle = (offerId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(offerId)) {
        next.delete(offerId);
      } else {
        next.add(offerId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      // Deselect every visible candidate, leave any out-of-filter picks alone.
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const offer of candidates) next.delete(offer.id);
        return next;
      });
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const offer of candidates) next.add(offer.id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedCount === 0) return;
    const selectedOffers = candidates.filter((offer) => selectedIds.has(offer.id));
    // Some selected ids may live outside the current search filter — resolve
    // those from the full offers list to be safe.
    const remaining = selectedIds.size - selectedOffers.length;
    const finalSelection =
      remaining > 0
        ? offers.filter((offer) => selectedIds.has(offer.id))
        : selectedOffers;

    setSubmitting(true);
    try {
      await onSubmit(finalSelection);
    } catch {
      // Caller is expected to surface its own error toast; we just keep the
      // dialog open so the user can retry.
    } finally {
      setSubmitting(false);
    }
  };

  const buttonLabel =
    submitLabel?.(selectedCount) ??
    (selectedCount === 0
      ? 'Add'
      : `Add ${selectedCount} ${selectedCount === 1 ? 'offer' : 'offers'}`);

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="flex max-h-[80vh] flex-col overflow-hidden p-0 sm:max-w-[600px]">
        <DialogHeader className="px-6 pb-3 pt-6">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search offers..."
              className="pl-9"
              autoFocus
            />
          </div>
        </DialogHeader>

        {!isLoading && candidates.length > 0 && (
          <div
            role="button"
            tabIndex={0}
            aria-pressed={allVisibleSelected}
            onClick={toggleSelectAll}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleSelectAll();
              }
            }}
            className="flex w-full cursor-pointer items-center gap-3 border-y bg-muted/30 px-6 py-2 text-sm font-medium hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            <Checkbox
              checked={selectAllChecked}
              tabIndex={-1}
              aria-label="Select all visible offers"
            />
            <span className="flex-1">
              {allVisibleSelected ? 'Deselect all' : 'Select all'}
              {search.trim() ? ' matching' : ''}
            </span>
            <span className="text-xs text-muted-foreground">
              {visibleSelectedCount} / {candidates.length}
            </span>
          </div>
        )}

        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-6 py-3">
          {isLoading && <Loading text="Loading offers..." size="sm" />}
          {!isLoading && candidates.length === 0 && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              {search.trim()
                ? `No offers match "${search.trim()}".`
                : emptyMessage ?? 'No offers available.'}
            </div>
          )}
          {candidates.map((offer) => {
            const checked = selectedIds.has(offer.id);
            return (
              <div
                key={offer.id}
                role="button"
                tabIndex={0}
                aria-pressed={checked}
                onClick={() => toggle(offer.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(offer.id);
                  }
                }}
                className={`flex w-full items-center gap-3 rounded-md border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer hover:bg-accent ${
                  checked ? 'border-primary bg-accent/40' : ''
                }`}
              >
                <Checkbox
                  checked={checked}
                  tabIndex={-1}
                  aria-label={`Select ${offerLabel(offer)}`}
                />
                {/* Click handling lives on the row, so the Checkbox is purely visual here. */}
                {offer.featuredImage?.url ? (
                  <img
                    src={offer.featuredImage.url}
                    alt={offerLabel(offer)}
                    className="h-10 w-14 shrink-0 rounded object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded bg-muted">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{offerLabel(offer)}</div>
                  {offer.title && offer.name && (
                    <div className="truncate text-xs text-muted-foreground">{offer.title}</div>
                  )}
                </div>
                {offer.status && <PromotionalOfferStatusBadge status={offer.status} />}
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 border-t px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            {selectedCount > 0 ? `${selectedCount} selected` : 'Select one or more offers'}
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={selectedCount === 0 || submitting} loading={submitting}>
              {buttonLabel}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
